<template>
  <div>
    <Breadcrumbs v-if="user"
      :breadcrumbs="[
        { title: this.$t('menu.configurations'), disabled: true },
        { title: this.$t('menu.users'), meta: { type: 'string', value: `/${authService.slug}/users` } },
        { title: user.name, disabled: true }
      ]">
      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <va-dropdown
          class="custom-dropdown"
          fixed>
          <span slot="anchor">
            <slot>
              <div class="pl-3 pr-2 clickable">
                <span class="fa fa-ellipsis-v" style="font-size: 1.5rem;"></span>
              </div>
            </slot>
          </span>
          <div class="contact-dropdown__content pl-3 pr-2 pt-2 pb-2">
            <div v-if="canUpdateUser" class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2" @click.prevent="showUpdateUser = true">
              <div>
                <i class="fa fa-pencil"></i>
                 {{$t('users.details.actions.edit')}}
              </div>
            </div>
            <div v-if="isFromEvollu" class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2" @click.prevent="showCloneUser = true">
              <div>
                <i class="fa fa-clone"></i>
                {{$t('users.details.actions.clone')}}
              </div>
            </div>
            <div v-if="isDevUser" class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2" @click.prevent="loginAs">
              <div>
                <i class="fa fa-lock"></i>
                {{$t('users.details.actions.login_as', {name: user.name})}}
              </div>
            </div>
            <div v-if="canBlockOrDeleteUser" class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2" @click.prevent="showBlockConfirmationModal = true">
              <div v-if="!user.blocked_at">
                <i class="fa fa-ban"></i>
                {{$t('users.details.actions.block')}}
              </div>
              <div v-else>
                <i class="fa fa-unlock"></i>
                {{$t('users.details.actions.unblock')}}
              </div>
            </div>
            <div v-if="canBlockOrDeleteUser"
                class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                @click.prevent="showDeleteConfirmationModal = true">
              <div>
                <i class="fa fa-trash"></i>
                 {{$t('users.details.actions.delete')}}
              </div>
            </div>
          </div>
        </va-dropdown>
      </div>
    </Breadcrumbs>

    <section v-if="user">

      <UserInfo
        class="mb-3"
        :user="user" />

      <!-- User Permissions Details Section -->
      <UserRoleDetails
        class="mb-3"
        :showTitle="true"
        :userId="user.id"
        :entityId="user.entity.id"
        :limitItems="7"
        @showEntitiesForRoleModal="showEntitiesForRoleModal"
        @showRolesForEntityModal="showRolesForEntityModal" />
      <!-- End User Permissions Details Section -->

      <!-- Notifications -->
      <Notifications :showPreviewToggle="true"/>

      <!-- End Notifications -->

      <!-- User Activities Details Section -->
      <UserActivityDateDetails
        id="user-activities"
        ref="userActivities"
        class="mb-3"
        :user="user"
        :activities="user.activities || []"
        :availableActivities="configurations.availableActivities"
        :editable="true"
        :showTitle="true"
        :showSection="hasPermissionsToActivities"
        @show-entities-for-activity-modal="showEntitiesForActivityModal" />
      <!-- End User Activities Details Section -->


    </section>

    <!-- Create Or Update User Modal -->
    <CreateOrUpdateUser
      v-if="showUpdateUser"
      :user="user"
      @cancel="showUpdateUser = false"
      @user-updated="onUserUpdated"
      />
    <!-- End Create Or Update User Modal -->

    <!-- Clone User Modal -->
    <CloneUserModal
      v-if="showCloneUser"
      :from="user"
      @modalClosed="closeClone" />
    <!-- End Clone User Modal -->

    <entities-by-role-full-details
      v-if="showEntitiesForRole"
      v-bind="entitiesForRoleProps"
      @entitiesByRoleModalClosed="closeModal">
    </entities-by-role-full-details>

    <roles-by-entity-full-details
      v-if="showRolesForEntity"
      v-bind="rolesForEntityProps"
      @rolesByEntityModalClosed="closeModal">
    </roles-by-entity-full-details>

    <entities-by-activity-full-details
      v-if="showEntitiesForActivity"
      v-bind="entitiesForActivityProps"
      @entitiesByActivityModalClosed="closeModal">
    </entities-by-activity-full-details>

    <!-- Delete Report Modal Confirmation -->
    <ConfirmationModal
      v-if="showBlockConfirmationModal"
      :title="user.blocked_at ? $t('users.details.unblock_title', {name: user.name}) : $t('users.details.block_title', {name: user.name})"
      :body="user.blocked_at ? $t('users.details.unblock_message') : $t('users.details.block_message')"
      @modal-confirmed="toggleBlockUser"
      @modal-canceled="showBlockConfirmationModal = false"
    />

    <ConfirmationModal
      v-if="showDeleteConfirmationModal"
      :title="$t('users.delete?')"
      :body="$t('users.confirm_delete', {name: this.user.name })"
      @modal-confirmed="confirmDeleteUser"
      @modal-canceled="showDeleteConfirmationModal = false" />
  </div>
</template>
<script>
import { UserService } from '@/api/AuthService'
import UserInfo from '@/components/users/UserInfo'
import IsFromEvollu from '@/mixins/IsFromEvolluMixin'
import { mapActions, mapState, mapGetters } from 'vuex'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import CloneUserModal from './components/CloneUserModal'
import UserRoleDetails from './components/Roles/UserRoleDetails'
import CreateOrUpdateUser from './components/CreateOrUpdateUser'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import UserActivityDetails from './components/Activities/UserActivityDetails'
import UserActivityDateDetails from './components/Activities/UserActivityDateDetails'
import EntitiesByRoleFullDetails from './components/Roles/components/EntitiesByRoleFullDetails'
import EntitiesByActivityFullDetails from './components/Activities/components/EntitiesByActivityFullDetails'
import RolesByEntityFullDetails from './components/Roles/components/RolesByEntityFullDetails'
import Notifications from './components/Notifications/Index'

const usersInfoType = 100
const activitiesInfoType = 140
const readAccessType = 1
const writeAccessType = 2

export default {
  components: {
    UserInfo,
    Breadcrumbs,
    CloneUserModal,
    UserRoleDetails,
    ConfirmationModal,
    UserActivityDetails,
    UserActivityDateDetails,
    CreateOrUpdateUser,
    RolesByEntityFullDetails,
    EntitiesByRoleFullDetails,
    EntitiesByActivityFullDetails,
    Notifications
  },
  mixins: [
    IsFromEvollu
  ],
  data () {
    return {
      user: null,
      showUpdateUser: false,
      showCloneUser: false,
      entitiesForRoleProps: {},
      showEntitiesForActivity: false,
      userWasRecentlyCreated: false,
      entitiesForActivityProps: {},
      entitiesForActivity: [],
      rolesForEntityProps: {},
      assignRolesProps: {},
      showEntitiesForRole: false,
      showRolesForEntity: false,
      showBlockConfirmationModal: false,
      showDeleteConfirmationModal: false,
      entitiesForRole: [],
      rolesForEntity: []
    }
  },
  created () {
    this.loadAvailableActivities()
  },
  methods: {
    ...mapActions([
      'loadAvailableActivities'
    ]),
    toggleBlockUser: function () {
      const action = this.user.blocked_at ? 'unblock' : 'block'

      UserService[action](this.user.id)
        .then(() => {
          this.loadUser(this.user.id)
          this.showToast(this.$t(`users.${action}_success`), this.$store.state.successToastOptions)
        }).catch(() => {
          this.showToast(this.$t('qls.something_went_wrong'), this.$store.state.errorToastOptions)
        }).finally(() => {
          this.showBlockConfirmationModal = false
        })
    },
    loginAs: function () {
      this.$http({
        url: this.$store.state.evollauth_server + '/users/' + this.user.id + '/token'
      }).then((response) => {
        window.location.reload()
      }).catch((error) => {
        console.log(error)
      })
    },
    onUserUpdated: function ({ user }) {
      this.user = user
      this.$refs.userActivities.$refs.entityActivityUsersTable.reload()
    },
    closeModal: function () {
      this.showUserCloneModal = false
      this.showEntitiesForRole = false
      this.showEntitiesForActivity = false
      this.showRolesForEntity = false
      this.entitiesForRole = []
      this.entitiesForActivity = []
    },
    closeClone: function () {
      this.showCloneUser = false
    },
    loadUser: function (userId) {
      UserService.get(userId)
        .then((data) => {
          this.user = data
        }).catch(() => {
          this.redirectBack()
        })
    },
    redirectBack: function () {
      this.$router.go(-1)
    },
    showEntitiesForRoleModal: function ({ entities, role }) {
      this.entitiesForRoleProps = {
        entities,
        role,
        user: this.user
      }
      this.entitiesForRole = entities
      this.showEntitiesForRole = true
    },
    showRolesForEntityModal: function ({ roles, entity }) {
      this.rolesForEntityProps = {
        roles,
        entity,
        user: this.user
      }
      this.showRolesForEntity = true
      this.rolesForEntity = roles
    },
    showEntitiesForActivityModal: function ({ entities, activity }) {
      this.entitiesForActivityProps = {
        entities,
        activity,
        user: this.user
      }
      this.showEntitiesForActivity = true
      this.entitiesForActivity = entities
    },
    confirmDeleteUser: function () {
      UserService.delete(this.user.id)
        .then(() => {
          this.showToast(this.$t('users.user_deleted'), this.$store.state.successToastOptions)
          this.showDeleteConfirmationModal = false
          setTimeout(() => {
            this.$router.push(`/${this.authService.slug}/users`)
          }, 500)
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('users.user_deletion_failed'), this.$store.state.errorToastOptions)
        })
    },
  },
  computed: {
    ...mapState([
      'configurations'
    ]),
    authService: function () {
      return this.$store.state.services.find(s => s.url === this.$store.state.evollauth_server) || null
    },
    writeUsersPermissions: function () {
      if (this.authService) {
        return this.authService.user_permissions.filter(p => p.info_type === usersInfoType && p.access_type >= writeAccessType)
      }
      return []
    },
    readUsersPermissions: function () {
      if (this.authService) {
        return this.authService.user_permissions.filter(p => p.info_type === usersInfoType && p.access_type >= readAccessType)
      }
      return []
    },
    hasPermissionsToActivities: function () {
      return this.authService.user_permissions.some(p => p.info_type === activitiesInfoType && p.access_type >= readAccessType)
    },
    canUpdateUser: function () {
      return this.authService && this.user && this.writeUsersPermissions.some(p => (p.entity_id === null || p.entity_id === this.user.entity_id))
    },
    canBlockOrDeleteUser: function () {
      return this.canUpdateUser && this.user.id !== this.$auth.user().id
    }
  },
  beforeRouteEnter (to, from, next) {
    if (to.params.id !== undefined) {
      next(vm => {
        vm.loadUser(parseInt(to.params.id))
        vm.from = from
      })
    } else {
      next(from)
    }
  },
  beforeRouteUpdate: function (to, from, next) {
    if (to.params.id !== undefined) {
      this.loadUser(parseInt(to.params.id))
    }
    next()
  }
}
</script>
<style lang="scss" scoped>
.profile-details-label {
  text-transform: capitalize;
}

.profile-details-description {
  text-transform: capitalize;
}

.dropdown-item-wrapper:hover {
  background-color: #d6d6d6;
  cursor: pointer;
}
</style>
